import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface TenantProfile {
  id: string;
  apiBaseUrl: string;
  marketId: string;
  marketName?: string;
  /** User-supplied label for this market/server entry, set during onboarding
   *  and shown wherever a market needs to be told apart from others (login
   *  screen, switch-profile list) instead of the server-resolved marketName. */
  profileName?: string;
  syncfusionKey?: string;
  vendorId?: string;
  vendorName?: string;
  token?: string;
  lastUsedAt?: number;
}

// Kept as an alias so existing call sites (User.service.ts, login.ts,
// push-notification.service.ts, guards) keep compiling unchanged — they only
// ever read fields that still exist on TenantProfile.
export type TenantConfig = TenantProfile;

export interface TenantProfilesStore {
  profiles: TenantProfile[];
  activeProfileId: string | null;
}

const STORAGE_KEY = 'tenant_profiles_v1';
const LEGACY_STORAGE_KEY = 'tenant_config_v1';

function computeProfileId(apiBaseUrl: string, marketId: string): string {
  return apiBaseUrl.trim().replace(/\/+$/, '').toLowerCase() + '::' + marketId.trim();
}

/**
 * One-time, idempotent migration from the old single-config storage format to
 * the new profiles list. Exported as a plain function (not an @Injectable
 * method) because main.ts runs before Angular DI exists at bootstrap.
 */
export async function migrateTenantStorage(): Promise<TenantProfilesStore> {
  const existing = await Preferences.get({ key: STORAGE_KEY });
  if (existing.value) {
    try {
      return JSON.parse(existing.value) as TenantProfilesStore;
    } catch {
      // fall through and rebuild below
    }
  }

  const legacy = await Preferences.get({ key: LEGACY_STORAGE_KEY });
  let store: TenantProfilesStore;

  if (legacy.value) {
    try {
      const oldCfg = JSON.parse(legacy.value) as TenantConfig;
      const id = computeProfileId(oldCfg.apiBaseUrl, oldCfg.marketId);
      const profile: TenantProfile = { ...oldCfg, id, lastUsedAt: Date.now() };
      store = { profiles: [profile], activeProfileId: id };
    } catch {
      store = { profiles: [], activeProfileId: null };
    }
  } else {
    store = { profiles: [], activeProfileId: null };
  }

  await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(store) });
  if (legacy.value) {
    await Preferences.remove({ key: LEGACY_STORAGE_KEY });
  }
  return store;
}

@Injectable({ providedIn: 'root' })
export class TenantService {
  private storeSubject = new BehaviorSubject<TenantProfilesStore>({ profiles: [], activeProfileId: null });
  store$ = this.storeSubject.asObservable();

  // Behavior-preserving surface — same signatures as before, now backed by
  // "the active profile" instead of a single saved config.
  config$ = this.store$.pipe(
    map(store => store.profiles.find(p => p.id === store.activeProfileId) ?? null)
  );

  constructor(private http: HttpClient) {}

  static computeProfileId(apiBaseUrl: string, marketId: string): string {
    return computeProfileId(apiBaseUrl, marketId);
  }

  async loadSavedConfig(): Promise<TenantConfig | null> {
    try {
      const store = await migrateTenantStorage();
      this.storeSubject.next(store);
      this.syncEnvironment(store);
      return store.profiles.find(p => p.id === store.activeProfileId) ?? null;
    } catch {
      return null;
    }
  }

  async saveConfig(cfg: TenantConfig): Promise<void> {
    await this.upsertProfile(cfg);
    await this.switchToProfile(cfg.id ?? computeProfileId(cfg.apiBaseUrl, cfg.marketId));
  }

  async clearConfig(): Promise<void> {
    const active = this.getActiveProfile();
    if (active) {
      await this.removeProfile(active.id);
    }
  }

  getConfig(): TenantConfig | null {
    const store = this.storeSubject.value;
    return store.profiles.find(p => p.id === store.activeProfileId) ?? null;
  }

  isConfigured(): boolean {
    return this.getConfig() !== null;
  }

  // ── Multi-profile API ────────────────────────────────────────────────────

  getAllProfiles(): TenantProfile[] {
    return [...this.storeSubject.value.profiles].sort(
      (a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0)
    );
  }

  getActiveProfile(): TenantProfile | null {
    return this.getConfig();
  }

  async upsertProfile(profile: Partial<TenantProfile> & { apiBaseUrl: string; marketId: string }): Promise<TenantProfile> {
    const id = profile.id ?? computeProfileId(profile.apiBaseUrl, profile.marketId);
    const store = this.storeSubject.value;
    const existingIndex = store.profiles.findIndex(p => p.id === id);
    const existing = existingIndex >= 0 ? store.profiles[existingIndex] : undefined;

    const merged: TenantProfile = { ...existing, ...profile, id, lastUsedAt: Date.now() };

    const profiles = [...store.profiles];
    if (existingIndex >= 0) {
      profiles[existingIndex] = merged;
    } else {
      profiles.push(merged);
    }

    await this.persist({ ...store, profiles });
    return merged;
  }

  async switchToProfile(id: string): Promise<boolean> {
    const store = this.storeSubject.value;
    if (!store.profiles.some(p => p.id === id)) return false;
    await this.persist({ ...store, activeProfileId: id });
    return true;
  }

  async removeProfile(id: string): Promise<void> {
    const store = this.storeSubject.value;
    const profiles = store.profiles.filter(p => p.id !== id);

    let activeProfileId = store.activeProfileId;
    if (activeProfileId === id) {
      const next = [...profiles].sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0))[0];
      activeProfileId = next?.id ?? null;
    }

    await this.persist({ profiles, activeProfileId });
  }

  private async persist(store: TenantProfilesStore): Promise<void> {
    await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(store) });
    this.storeSubject.next(store);
    this.syncEnvironment(store);
  }

  /**
   * On native, keep environment.apiBaseUrl pointed at the active profile's
   * server. Services such as GeneralService / InventoryService read
   * environment.apiBaseUrl directly, so this keeps them correct after an app
   * restart and after switching profiles.
   */
  private syncEnvironment(store: TenantProfilesStore): void {
    if (!Capacitor.isNativePlatform()) return;
    const active = store.profiles.find(p => p.id === store.activeProfileId);
    if (active?.apiBaseUrl) {
      environment.apiBaseUrl = active.apiBaseUrl;
    }
  }

  // ── Server helpers ───────────────────────────────────────────────────────

  /**
   * Derives the web-app origin from the API base URL.
   * e.g. "https://srv.com/api" → "https://srv.com"
   */
  getOriginFromApiUrl(apiBaseUrl: string): string {
    return apiBaseUrl.replace(/\/api\/?$/i, '');
  }

  /**
   * Validates a server URL by calling GetMarketName and returns the market name.
   * Used during onboarding to confirm the server+marketId pair is valid.
   */
  async validateServer(apiBaseUrl: string, marketId: string): Promise<string> {
    const base = apiBaseUrl.replace(/\/+$/, '');
    const result = await firstValueFrom(
      this.http.get<any>(`${base}/General/GetMarketName?marketId=${marketId}`)
    );
    return result?.Message || result?.MarketName || result?.Name || marketId;
  }

  /**
   * Fetches the Syncfusion license key from the server's config.json.
   * Falls back silently if the server doesn't expose it.
   */
  async fetchSyncfusionKey(apiBaseUrl: string): Promise<string | null> {
    try {
      const origin = this.getOriginFromApiUrl(apiBaseUrl);
      const cfg = await firstValueFrom(
        this.http.get<any>(`${origin}/assets/config/config.json`)
      );
      return cfg?.syncfusionKey ?? null;
    } catch {
      return null;
    }
  }
}